import type { PlanChoice, SetupRequirements } from '@dashboard/types/user'

export type BillingTier = PlanChoice['tier']
export type BillingInterval = PlanChoice['interval']
export type SetupRequirementItem = SetupRequirements['items'][number]

const TIER_ORDER: readonly BillingTier[] = ['grow', 'scale', 'pro']

export const TIER_NAMES: Record<BillingTier, string> = { grow: 'Grow', scale: 'Scale', pro: 'Pro' }

/** Position in Grow → Scale → Pro; unknown tiers sort last. */
export function tierRank(tier: string): number {
  const index = TIER_ORDER.indexOf(tier as BillingTier)
  return index === -1 ? TIER_ORDER.length : index
}

/** The cheapest plan that covers the setup, never below Grow. */
export function requiredTier(requirements: SetupRequirements | null | undefined): BillingTier {
  return requirements?.required_tier ?? 'grow'
}

/**
 * DECISIONS 3: a /pricing pick wins (plan and interval) even when the setup
 * needs more; otherwise the cheapest plan that covers the setup, billed monthly.
 */
export function preselectPlan(
  pick: PlanChoice | null | undefined,
  requirements: SetupRequirements | null | undefined
): PlanChoice {
  if (pick) {
    return { tier: pick.tier, interval: pick.interval }
  }
  return { tier: requiredTier(requirements), interval: 'monthly' }
}

/** Badge on a plan row: the /pricing pick, or the plan the setup resolves to. */
export function planBadge(
  tier: string,
  pick: PlanChoice | null | undefined,
  requirements: SetupRequirements | null | undefined
): string | null {
  if (pick) {
    return pick.tier === tier ? 'You picked this' : null
  }
  const required = requiredTier(requirements)
  if (tier !== required) {
    return null
  }
  return required === 'grow' ? 'Covers your setup' : 'Needed for your setup'
}

/** Setup items that the selected plan doesn't include (they switch off at the deadline). */
export function itemsAboveTier(
  selected: string,
  requirements: SetupRequirements | null | undefined
): SetupRequirementItem[] {
  const rank = tierRank(selected)
  return (requirements?.items ?? []).filter((item) => tierRank(item.tier) > rank)
}

/** "a, b and c" */
export function joinItems(labels: string[]): string {
  if (labels.length <= 1) {
    return labels[0] ?? ''
  }
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
}
